import { ENTITY_KINDS, SCHEMA_VERSION, SYNC_PROTOCOL_VERSION } from '@doneline/shared';
import { z } from 'zod';

export const entityKindSchema = z.enum(ENTITY_KINDS);
export const opKindSchema = z.enum(['upsert', 'delete']);

const uuid = z.string().uuid();

/**
 * entity_id 校验：大多数实体是单个 UUID，但 `task_tag` 这种联结实体用
 * 复合主键 `<task_id>:<tag_id>`（见 apps/web/src/lib/store.ts 与
 * apps/api/src/services/sync-push.ts 的 split 逻辑）。
 *
 * 历史 bug：曾经写成 `z.string().uuid()`，导致任何含 task_tag 的 push 批次
 * 被 Fastify body 校验整批 400 → 标签永远同步不出去（apps/api/tests 直调
 * applyPush 绕过路由 schema，所以没测到）。这里放宽为「1~2 段、每段都是 UUID」。
 *
 * 注意：复合两段形式**只属于 task_tag**——这一层（字符串 schema）看不到
 * entity_kind，按 kind 收紧的校验在 pushOpSchema 的 superRefine 里做。
 */
export const entityIdSchema = z.string().refine(
  (v) => {
    const parts = v.split(':');
    return parts.length >= 1 && parts.length <= 2 && parts.every((p) => uuid.safeParse(p).success);
  },
  { message: 'entity_id must be a UUID or composite "<uuid>:<uuid>" (task_tag)' },
);

export const pushOpSchema = z
  .object({
    op_id: z.string().uuid(),
    entity_kind: entityKindSchema,
    entity_id: entityIdSchema,
    op_kind: opKindSchema,
    base_row_version: z.number().int().nullable(),
    client_ts: z.string().datetime({ offset: true }),
    fields: z.record(z.string(), z.unknown()).default({}),
  })
  .superRefine((op, ctx) => {
    // 复合 "uuid:uuid" 只对 task_tag 合法。其他实体的复合 id 会穿透到 DB 层
    // 的 uuid cast 错误（22P02），在没有 per-op savepoint 之前正好把整个
    // push 事务投毒（25P02），见 apps/api/src/services/sync-push.ts。
    if (op.entity_kind !== 'task_tag' && op.entity_id.includes(':')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entity_id'],
        message: `composite entity_id is only allowed for task_tag, got entity_kind "${op.entity_kind}"`,
      });
    }
  });

export type PushOp = z.infer<typeof pushOpSchema>;

export const syncProtocolVersionSchema = z.preprocess(
  (value) => {
    if (value === undefined) return 1;
    if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
    return value;
  },
  z.union([z.literal(1), z.literal(SYNC_PROTOCOL_VERSION)]),
);

export const pushRequestSchema = z
  .object({
    schema_version: z.number().int().default(SCHEMA_VERSION),
    // Missing means legacy v1. This default is intentional: deployed v1 clients
    // must remain parseable so the route can return an explicit 426 after GC.
    protocol_version: syncProtocolVersionSchema,
    device_id: z.string().uuid(),
    // v2 clients must explicitly send the cursor they have durably committed.
    // Defaulting a missing legacy field to zero made v1 look retention-capable.
    last_persisted_cursor: z.number().int().nonnegative().optional(),
    client_clock_ts: z.string().datetime({ offset: true }),
    ops: z.array(pushOpSchema).max(500),
  })
  .superRefine((request, ctx) => {
    if (
      request.protocol_version === SYNC_PROTOCOL_VERSION &&
      request.last_persisted_cursor === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['last_persisted_cursor'],
        message: 'last_persisted_cursor is required by sync protocol v2',
      });
    }
  });

type ParsedPushRequest = z.output<typeof pushRequestSchema>;
export type PushRequest = Omit<ParsedPushRequest, 'protocol_version'> & {
  protocol_version?: 1 | typeof SYNC_PROTOCOL_VERSION;
};

export const pushOutcomeSchema = z.enum(['applied', 'ignored_deleted', 'needs_full_rebase']);

export const pushAcceptedSchema = z.object({
  op_id: z.string().uuid(),
  entity_id: entityIdSchema,
  entity_kind: entityKindSchema,
  new_row_version: z.number().int(),
  server_ts: z.string().datetime({ offset: true }),
  // 行落库后的真实 DB updated_at（bump_row_version 触发器写的 Postgres now() =
  // 事务开始时刻），与 pull 行的 updated_at **同源可比**。server_ts 是服务端
  // UPDATE 之后取的 JS 墙钟，恒晚于触发器时刻——客户端若拿它 rebase 本地
  // updatedAt，本地时钟会结构性大于随后 pull 回来的行时钟，mergeList 的严格
  // isNewer 会把携带另一设备并发字段编辑的合并行判旧丢弃（游标已推进不重发 =
  // 跨端静默丢字段）。客户端 rebase 一律用本字段；没有行值可回传的分支（op_id
  // 幂等重放 / ledger 短路）省略，客户端跳过 rebase（宁可保守不动本地时钟）。
  // wire 兼容：旧客户端忽略新字段，新客户端对缺失字段跳过。
  row_updated_at: z.string().datetime({ offset: true }).optional(),
  // Optional only for the legacy v1 response contract. Protocol v2 requires it
  // so clients can repair local phantom rows / partial-upsert ledger conflicts.
  outcome: pushOutcomeSchema.optional(),
});

export const pushResponseSchema = z.object({
  schema_version: z.number().int(),
  accepted: z.array(pushAcceptedSchema),
  rejected: z
    .array(
      z.object({
        op_id: z.string().uuid(),
        reason: z.string(),
      }),
    )
    .default([]),
  server_row_version_high_watermark: z.number().int(),
});

export type PushResponse = z.infer<typeof pushResponseSchema>;

export const pushV2ResponseSchema = pushResponseSchema.extend({
  protocol_version: z.literal(SYNC_PROTOCOL_VERSION),
  accepted: z.array(pushAcceptedSchema.extend({ outcome: pushOutcomeSchema })),
});

export type PushV2Response = z.infer<typeof pushV2ResponseSchema>;

export const pullQuerySchema = z.object({
  // Missing is a deployed legacy v1 client. See the push-side comment above.
  protocol_version: syncProtocolVersionSchema,
  since_version: z.coerce.number().int().nonnegative().default(0),
  limit: z.coerce.number().int().min(1).max(2000).default(500),
  device_id: z.string().uuid().optional(),
  snapshot: z
    .preprocess((v) => v === true || v === 'true' || v === '1', z.boolean())
    .default(false),
  // replace-snapshot 首页由服务端钉住的用户级高水位。后续页必须原样携回，
  // 防止分页期间的新写入被末页 cursor 越过却从未出现在快照中。
  snapshot_cutoff: z.coerce.number().int().nonnegative().optional(),
  // 客户端当前的 App 语言，仅用于首次 bootstrap 时服务端发放引导数据的文案语言
  // （migration 020 / services/onboarding-seed.ts）。可选：老客户端不带 → 回落 en。
  // 不能改用 user_profile.settings.language —— 新用户那一行是注册触发器建的，
  // settings 还是 '{}'，第一次 bootstrap 时根本没有语言可读。
  locale: z.string().min(2).max(35).optional(),
});

type ParsedPullQuery = z.output<typeof pullQuerySchema>;
export type PullQuery = Omit<ParsedPullQuery, 'protocol_version'> & {
  protocol_version?: 1 | typeof SYNC_PROTOCOL_VERSION;
};

export const pullResponseSchema = z.object({
  schema_version: z.number().int(),
  changes: z.object({
    user_profile: z.array(z.record(z.string(), z.unknown())).default([]),
    project: z.array(z.record(z.string(), z.unknown())).default([]),
    section: z.array(z.record(z.string(), z.unknown())).default([]),
    task: z.array(z.record(z.string(), z.unknown())).default([]),
    comment: z.array(z.record(z.string(), z.unknown())).default([]),
    tag: z.array(z.record(z.string(), z.unknown())).default([]),
    task_tag: z.array(z.record(z.string(), z.unknown())).default([]),
  }),
  next_cursor: z.number().int(),
  has_more: z.boolean(),
  snapshot_required: z.boolean().default(false),
  snapshot_cutoff: z.number().int().nonnegative().optional(),
  server_ts: z.string().datetime({ offset: true }),
});

export type PullResponse = z.infer<typeof pullResponseSchema>;

export const pullV2ResponseSchema = pullResponseSchema.extend({
  protocol_version: z.literal(SYNC_PROTOCOL_VERSION),
});

export type PullV2Response = z.infer<typeof pullV2ResponseSchema>;

export const syncProtocolUpgradeRequiredSchema = z.object({
  error: z.literal('sync_protocol_upgrade_required'),
  minimum_protocol_version: z.literal(SYNC_PROTOCOL_VERSION),
});

export const healthResponseSchema = z.object({
  schema_version: z.number().int(),
  server_ts: z.string().datetime({ offset: true }),
  server_row_version_high_watermark: z.number().int(),
});

export type HealthResponse = z.infer<typeof healthResponseSchema>;
